/**
 * @Author:
 * @Description: AI 助手悬浮球
 * @DateTime: Created in 10:55 2026/8/27
 */

import * as Vue from "vue-tsx-support";
import { Component } from 'vue-property-decorator'
import "vue-tsx-support/enable-check"
import './aiAssistant.less';
import Drawer from 'src/types/iview/Drawer';
import Input from 'src/types/iview/Input';
import Button from 'src/types/iview/Button';
import AiAssistantApi from './api/aiAssistantApi';

interface MessageI {
	role: 'user' | 'assistant';
	content: string;
}

@Component
export default class AiAssistant extends Vue.Component<any> {
	visible: boolean = false;
	inputValue: string = '';
	sessionId: string | number = '';
	loading: boolean = false;
	messages: MessageI[] = [];
	suggestions: string[] = [
		'我有多少个监测站点？',
		'最近有什么报警？',
		'哪些站点当前超标？',
		'本周水质排名如何？'
	];

	open() {
		this.visible = true;
	}

	close() {
		this.visible = false;
	}

	onInputChange(value: string) {
		this.inputValue = value;
	}

	onSuggestionClick(text: string) {
		this.inputValue = text;
	}

	async send() {
		let message = this.inputValue.trim();
		if (!message) {
			return;
		}
		this.messages.push({ role: 'user', content: message });
		this.inputValue = '';
		this.loading = true;

		let res = await AiAssistantApi.chat({ message, sessionId: this.sessionId });
		this.loading = false;

		if (res.code === 0 && res.data) {
			this.sessionId = res.data.sessionId || this.sessionId;
			this.messages.push({ role: 'assistant', content: res.data.answer || '' });
		} else {
			this.messages.push({ role: 'assistant', content: res.message || '服务异常' });
		}
	}

	onEnter() {
		this.send();
	}

	// 行内 markdown：加粗、行内代码
	renderInline(text: string): string {
		if (!text) {
			return '';
		}
		return text
			.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
			.replace(/`([^`]+)`/g, '<code>$1</code>');
	}

	// 轻量 markdown 渲染：支持代码块、标题、表格、列表、引用、分隔线、加粗、行内代码、换行
	// 注意：普通行之间必须补 <br>，否则相邻两行会连成一串（表现为"文字黏在一起"）
	renderMarkdown(content: string): string {
		if (!content) {
			return '';
		}
		// 兼容「同行围栏」代码块：```报文原文``` 这种写在同一条行里的形式，先规范成三行再解析。
		// 模型经常这样输出；不规范化会把下一行的说明文字误当成代码内容，并丢掉报文原文。
		let normalized = String(content).replace(/```([^`\n]+)```/g, '```\n$1\n```');
		let text = normalized
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');

		let lines = text.split('\n');
		let html: string[] = [];
		let i = 0;
		while (i < lines.length) {
			let line = lines[i];

			// ``` 代码块（报文原文），保留原始换行
			if (/^\s*```/.test(line)) {
				let buf: string[] = [];
				i++;
				while (i < lines.length && !/^\s*```/.test(lines[i])) {
					buf.push(lines[i]);
					i++;
				}
				i++;
				html.push(`<pre class="ai-md-pre"><code>${buf.join('\n')}</code></pre>`);
				continue;
			}

			// 分隔线
			if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
				html.push('<hr class="ai-md-hr">');
				i++;
				continue;
			}

			// 标题（### 一、上报间隔）
			let head = /^\s*(#{1,6})\s+(.*)$/.exec(line);
			if (head) {
				let tag = head[1].length <= 2 ? 'h4' : 'h5';
				html.push(`<${tag} class="ai-md-h">${this.renderInline(head[2].trim())}</${tag}>`);
				i++;
				continue;
			}

			if (/^\s*\|.*\|\s*$/.test(line)) {
				let rows: string[][] = [];
				while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
					let cells = lines[i].trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
					let isSep = cells.every((c) => /^:?-+:?$/.test(c));
					if (!isSep) {
						rows.push(cells);
					}
					i++;
				}
				if (rows.length > 0) {
					let header = rows[0];
					let bodyRows = rows.slice(1);
					let table = '<table class="ai-md-table"><thead><tr>';
					header.forEach((h) => { table += `<th>${this.renderInline(h)}</th>`; });
					table += '</tr></thead><tbody>';
					bodyRows.forEach((r) => {
						table += '<tr>';
						r.forEach((c) => { table += `<td>${this.renderInline(c)}</td>`; });
						table += '</tr>';
					});
					table += '</tbody></table>';
					html.push(table);
					continue;
				}
			}

			// 引用（注意：正文在最前面已做过 HTML 转义，这里的 ">" 已经变成 "&gt;"）
			if (/^\s*&gt;\s?/.test(line)) {
				let buf: string[] = [];
				while (i < lines.length && /^\s*&gt;\s?/.test(lines[i])) {
					buf.push(this.renderInline(lines[i].replace(/^\s*&gt;\s?/, '')));
					i++;
				}
				html.push(`<blockquote class="ai-md-quote">${buf.join('<br>')}</blockquote>`);
				continue;
			}

			// 无序列表
			if (/^\s*[-*+]\s+/.test(line)) {
				let buf: string[] = [];
				while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
					buf.push(`<li>${this.renderInline(lines[i].replace(/^\s*[-*+]\s+/, ''))}</li>`);
					i++;
				}
				html.push(`<ul class="ai-md-ul">${buf.join('')}</ul>`);
				continue;
			}

			// 有序列表
			if (/^\s*\d+[.)]\s+/.test(line)) {
				let buf: string[] = [];
				while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
					buf.push(`<li>${this.renderInline(lines[i].replace(/^\s*\d+[.)]\s+/, ''))}</li>`);
					i++;
				}
				html.push(`<ol class="ai-md-ol">${buf.join('')}</ol>`);
				continue;
			}

			// 空行 → 段落间距
			if (!line.trim()) {
				html.push('<div class="ai-md-gap"></div>');
				i++;
				continue;
			}

			// 普通行：行尾补 <br>，保证每一行单独成行
			html.push(this.renderInline(line) + '<br>');
			i++;
		}
		return html.join('');
	}

	render(h) {
		return (
			<div class="ai-assistant-wrapper">
				<div class="ai-assistant-float-btn" on-click={this.open}>
					AI 助手
				</div>
				<Drawer
					title="水质监测助手"
					width="420"
					placement="right"
					mask-closable={false}
					v-model={this.visible}
					on-on-close={this.close}
					class-name="ai-assistant-drawer"
				>
					<div class="ai-assistant-chat-list">
						{this.messages.length === 0 && (
							<div class="ai-assistant-suggestions">
								<div class="ai-assistant-suggestions-title">试试问：</div>
								{this.suggestions.map((text, index) => (
									<div key={index} class="ai-assistant-suggestion-item" on-click={() => this.onSuggestionClick(text)}>
										{text}
									</div>
								))}
							</div>
						)}
						{this.messages.map((item, index) => (
							<div key={index} class={['ai-assistant-message', `ai-assistant-message--${item.role}`]}>
								{item.role === 'assistant' ? (
									<div class="ai-assistant-message-content" domPropsInnerHTML={this.renderMarkdown(item.content)}></div>
								) : (
									<div class="ai-assistant-message-content">{item.content}</div>
								)}
							</div>
						))}
					</div>
					<div class="ai-assistant-input-wrapper">
						<Input
							value={this.inputValue}
							placeholder="请输入问题..."
							disabled={this.loading}
							on-input={this.onInputChange}
							on-on-enter={this.onEnter}
						/>
						<Button type="primary" loading={this.loading} on-click={this.send}>发送</Button>
					</div>
				</Drawer>
			</div>
		);
	}
}
